import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation, useParams} from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TextField,
  Pagination,
  Box,
  Button,
  Grid,
  Select,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  Menu,
  Snackbar,
  Alert,
  AlertTitle,
  useMediaQuery,
  useTheme,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import { Html5QrcodeScanner } from 'html5-qrcode';
import { PageTitle } from '../../../_metronic/layout/core';
import BackButton from '../../components/BackButton/BackButton';
import { LoadingBackdrop } from '../../components/LoadingSpinner/LoadingSpinner';
import {
  getAsistenciasEvento,
  buscarCandidatoAsistencia,
  confirmarAsistenciaCandidato,
} from '../../modules/apps/user-management/users-list/core/_requests';
import { getErrorMessage } from './EventosEmpleabilidad.helpers';
import styleScss from './styles.module.scss';
import { LISTA_TIPO_DOCUMENTO_CANDIDATO } from '../../models/TipoDocumentoOption';
import {EventoEmpleabilidad,} from './EventosEmpleabilidad.helpers';

export interface AsistenteRow {
  id?: number;
  user_id: number;
  tipo_documento: string;
  numero_documento: string;
  nombre_completo: string;
  realizo_prerregistro: 'SI' | 'NO';
  fecha_prerregistro?: string | null;
  fecha_asistencia?: string | null;
  metodo_confirmacion?: string | null;
  ya_asistio_hoy?: boolean;
}

export const RegistroAsistenciaEventoPage: React.FC = () => {
  const params = useParams<{ id?: string; eventoId?: string; idEvento?: string }>();
  const id = params.id || params.eventoId || params.idEvento;
  const location = useLocation();
  const evento = (location.state as { evento?: EventoEmpleabilidad } | null)?.evento;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Estados de Búsqueda Directa (Superior)
  const [tipoDocBusqueda, setTipoDocBusqueda] = useState<string>('CC');
  const [numeroDocBusqueda, setNumeroDocBusqueda] = useState<string>('');

  // Estados de Filtro de Grilla (Inferior)
  const [filtroPrerregistro, setFiltroPrerregistro] = useState<string>('Cualquiera');
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Estados de Tabla y Paginación
  const [asistentes, setAsistentes] = useState<AsistenteRow[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);

  // Estados de Menú de Acciones
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRow, setSelectedRow] = useState<AsistenteRow | null>(null);

  // Estados de Notificación Toast (Esquina Superior Derecha)
  const [open, setOpen] = useState(false);
  const [alert, setAlert] = useState(true);
  const [alertMsg, setAlertMsg] = useState('');

  // -------------------------------------------------------------
  // ESTADOS Y REFERENCIAS PARA EL LECTOR DE CÓDIGO QR
  // -------------------------------------------------------------
  const [openQrModal, setOpenQrModal] = useState<boolean>(false);
  const scannerRef = useRef<Html5QrcodeScanner | null>(null);
  const requierePrerregistro = (evento?: EventoEmpleabilidad) => evento?.requiere_prerregistro === 'SI';
  const notify = (msg: string, success = true) => {
    setAlert(success);
    setAlertMsg(msg);
    setOpen(true);
    setTimeout(() => setOpen(false), 3000);
  };

  const limpiarFiltros = () => {
    setSearchTerm('');
    setFiltroPrerregistro('Cualquiera');
    setPage(1);
  };

  // Abrir / Cerrar Modal QR
  const handleOpenQrModal = () => setOpenQrModal(true);

  const handleCloseQrModal = () => {
    if (scannerRef.current) {
      scannerRef.current.clear().catch((error) => console.error('Error al limpiar escáner:', error));
      scannerRef.current = null;
    }
    setOpenQrModal(false);
  };

  // Procesamiento de lectura exitosa del QR
  const handleScanSuccess = async (decodedText: string) => {
    handleCloseQrModal();
    notify(`QR detectado: ${decodedText}`);

    // Extrae el ID de usuario del texto leído (Ej. 'E-15-U-849' -> '849')
    let userId = decodedText;
    if (decodedText.includes('U-')) {
      userId = decodedText.split('U-')[1];
    }

    setLoading(true);
    try {
      const res = await buscarCandidatoAsistencia(id!, { user_id: userId });
      const candidato = res.data?.data || res.data;

      const nuevaFila: AsistenteRow = {
        id: candidato.asistencia_id || undefined,
        user_id: candidato.user_id,
        tipo_documento: candidato.tipo_doc,
        numero_documento: candidato.numero_doc,
        nombre_completo: candidato.nombre_completo,
        realizo_prerregistro: candidato.realizo_prerregistro,
        fecha_prerregistro: candidato.fecha_prerregistro,
        fecha_asistencia: candidato.fecha_asistencia_previa || (candidato.ya_asistio_hoy ? 'Asistió hoy' : null),
        ya_asistio_hoy: candidato.ya_asistio_hoy,
        metodo_confirmacion: 'QR',
      };

      setAsistentes((prev) => {
        const filtrados = prev.filter((item) => item.user_id !== nuevaFila.user_id);
        return [nuevaFila, ...filtrados];
      });

      notify('Candidato ubicado en la lista desde lectura de QR');
    } catch (err: any) {
      console.error('Error al procesar QR:', err);
      notify(getErrorMessage(err), false);
    } finally {
      setLoading(false);
    }
  };

  // Efecto que activa la cámara al desplegar la ventana emergente
  useEffect(() => {
    if (openQrModal) {
      const timeoutId = setTimeout(() => {
        const scanner = new Html5QrcodeScanner(
          'qr-reader-container',
          { fps: 10, qrbox: { width: 250, height: 250 } },
          false
        );

        scanner.render(
          (decodedText) => handleScanSuccess(decodedText),
          () => {} // Ignorar errores por cuadros sin código
        );

        scannerRef.current = scanner;
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  }, [openQrModal]);

  // 1. Cargar lista de asistentes confirmados desde el backend
  const fetchAsistencias = useCallback(async () => {
    if (!id) return;
    setLoading(true);

    try {
      const paramsQuery: any = { page, limit: 10 };
      if (searchTerm.trim()) paramsQuery.search = searchTerm.trim();
      if (filtroPrerregistro !== 'Cualquiera') paramsQuery.realizo_prerregistro = filtroPrerregistro;

      const res = await getAsistenciasEvento(id, paramsQuery);
      const payload = res?.data || res;

      setAsistentes(payload?.data || []);
      setTotalPages(payload?.totalPages || 1);
    } catch (err: any) {
      console.error('Error cargando asistencias:', err);
      setAsistentes([]);
      notify(getErrorMessage(err), false);
    } finally {
      setLoading(false);
    }
  }, [id, page, searchTerm, filtroPrerregistro]);

  useEffect(() => {
    fetchAsistencias();
  }, [fetchAsistencias]);

  // 2. Buscar candidato por Tipo + Número de Doc (Acción del botón Verde "Buscar")
  const handleBuscarCandidato = async () => {
    if (!id || !numeroDocBusqueda.trim()) {
      notify(!id ? 'El ID del evento no está disponible' : 'Ingrese un número de documento válido', false);
      return;
    }

  setLoading(true);
  try {
    const res = await buscarCandidatoAsistencia(id, {
      tipo_doc: tipoDocBusqueda,
      numero_doc: numeroDocBusqueda.trim(),
    });

    const candidato = res.data?.data || res.data;

    const nuevaFila: AsistenteRow = {
      id: candidato.asistencia_id || undefined,
      user_id: candidato.user_id,
      tipo_documento: candidato.tipo_doc,
      numero_documento: candidato.numero_doc,
      nombre_completo: candidato.nombre_completo,
      realizo_prerregistro: candidato.realizo_prerregistro,
      fecha_prerregistro: candidato.fecha_prerregistro,
      fecha_asistencia: candidato.fecha_asistencia_previa || (candidato.ya_asistio_hoy ? 'Asistió hoy' : null),
      ya_asistio_hoy: candidato.ya_asistio_hoy,
    };

    setAsistentes((prev) => {
      const filtrados = prev.filter((item) => item.user_id !== nuevaFila.user_id);
      return [nuevaFila, ...filtrados];
    });

    notify('Candidato ubicado en la lista para confirmación');
  } catch (err: any) {
    console.error('Error al buscar candidato:', err);
    notify(getErrorMessage(err), false);
  } finally {
    setLoading(false);
  }
};

  const asistioHoy = (row: AsistenteRow): boolean => {
    if (row.ya_asistio_hoy) return true;
    if (!row.fecha_asistencia) return false;
    const fecha = new Date(
      row.fecha_asistencia.includes('T') ? row.fecha_asistencia : row.fecha_asistencia.replace(/-/g, '/')
    );
    return !isNaN(fecha.getTime()) && fecha.toDateString() === new Date().toDateString();
  };

  // 3. Confirmar asistencia a través de la API
  const handleConfirmarAsistencia = async (candidato: AsistenteRow) => {
    if (!id) {
      notify('El ID del evento no está disponible', false);
      return;
    }

    setAnchorEl(null);

    if (asistioHoy(candidato)) {
      notify('El candidato ya cuenta con registro de asistencia en este evento', false);
      return;
    }

    setLoading(true);

    try {
      await confirmarAsistenciaCandidato(id!, {
        user_id: candidato.user_id,
        metodo_confirmacion: 'DOCUMENTO',
      });

      notify('Asistencia confirmada exitosamente');
      fetchAsistencias();
    } catch (err: any) {
      console.error('Error al confirmar asistencia:', err);
      notify(getErrorMessage(err), false);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, row: AsistenteRow) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const formatFecha = (fechaStr?: string | null): string => {
    if (!fechaStr) return '-';
    const fecha = new Date(fechaStr.includes('T') ? fechaStr : fechaStr.replace(/-/g, '/'));
    return isNaN(fecha.getTime()) ? fechaStr : fecha.toLocaleDateString('es-CO');
  };

  return (
    <>
      <PageTitle breadcrumbs={[]}>Registro Asistencia Evento</PageTitle>

      <div
        className="p-4 shadow-4 rounded-3 mt-15"
        style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '8px',
          boxShadow: '0px 3px 6px #00000029',
        }}
      >
        <LoadingBackdrop loading={loading} />

        {/* Encabezado Principal y Botón Regresar */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <BackButton />

            <Typography
              variant="h6"
              component="h1"
              sx={{
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 700,
                color: '#000305',
                fontSize: '1.25rem',
              }}
            >
              REGISTRO ASISTENCIA CANDIDATO AL EVENTO
            </Typography>
          </Box>
        </Box>

        {/* BARRA SUPERIOR: Controles de Búsqueda y Lectura de QR */}
        <Box
          sx={{
            mb: 3,
            p: { xs: 2, sm: 2.5 },
            backgroundColor: '#F8FAFC',
            borderRadius: '8px',
            border: '1px solid #E2E8F0',
            fontFamily: 'Poppins, sans-serif',
          }}
        >
          <Grid container spacing={2} alignItems="flex-end">
            {/* Tipo de identificación */}
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ width: '100%' }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'Poppins, sans-serif',
                    fontWeight: 600,
                    mb: 0.75,
                    color: '#000305',
                    fontSize: '14px',
                  }}
                >
                  Tipo de identificación
                </Typography>

                <Select
                  size="small"
                  fullWidth
                  value={tipoDocBusqueda}
                  onChange={(e) => setTipoDocBusqueda(e.target.value)}
                >
                  {LISTA_TIPO_DOCUMENTO_CANDIDATO.map((doc) => (
                    <MenuItem key={doc.code} value={doc.code}>
                      {doc.value}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </Grid>

            {/* Número de documento */}
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ width: '100%' }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'Poppins, sans-serif',
                    fontWeight: 600,
                    mb: 0.75,
                    color: '#000305',
                    fontSize: '14px',
                  }}
                >
                  Número de documento
                </Typography>

                <TextField
                  size="small"
                  fullWidth
                  value={numeroDocBusqueda}
                  onChange={(e) => setNumeroDocBusqueda(e.target.value)}
                  sx={{
                    backgroundColor: '#FFF',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '4px',
                    },
                    '& .MuiInputBase-input': {
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: '14.5px',
                    },
                  }}
                />
              </Box>
            </Grid>

            {/* Botón Buscar */}
            <Grid item xs={12} sm={6} md={2.5}>
              <Button
                variant="contained"
                fullWidth
                onClick={handleBuscarCandidato}
                sx={{
                  height: '40px',
                  backgroundColor: '#2266CC',
                  '&:hover': {
                    backgroundColor: '#1B52A4',
                  },
                  textTransform: 'none',
                  fontFamily: 'Poppins, sans-serif',
                  fontWeight: 600,
                  fontSize: '14.5px',
                  borderRadius: '4px',
                  boxShadow: 'none',
                }}
              >
                Buscar
              </Button>
            </Grid>

            {/* Botón Escanear QR */}
            <Grid item xs={12} sm={6} md={3.5}>
              <Button
                variant="contained"
                fullWidth
                startIcon={<QrCodeScannerIcon />}
                onClick={handleOpenQrModal}
                disabled={!requierePrerregistro(evento)}
                sx={{
                  height: '40px',
                  backgroundColor: '#2266CC',
                  '&:hover': {
                    backgroundColor: '#1B52A4',
                  },
                  textTransform: 'none',
                  fontFamily: 'Poppins, sans-serif',
                  fontWeight: 600,
                  fontSize: '14.5px',
                  borderRadius: '4px',
                  whiteSpace: 'nowrap',
                  boxShadow: 'none',
                }}
              >
                Escanear QR preregistro
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* MODAL PARA LA LECTURA DE QR CON CÁMARA */}
        <Dialog open={openQrModal} onClose={handleCloseQrModal} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600 }}>
              Escanear Código QR
            </Typography>
            <IconButton onClick={handleCloseQrModal}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', textAlign: 'center' }}>
                Apunta la cámara de tu equipo hacia el código QR de preregistro del candidato.
              </Typography>
              <div id="qr-reader-container" style={{ width: '100%' }}></div>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseQrModal} color="secondary">
              Cancelar
            </Button>
          </DialogActions>
        </Dialog>

        {/* BARRA INFERIOR DE FILTROS EN LA GRILLA */}
        <Box
          sx={{
            mb: 2,
            fontFamily: 'Poppins, sans-serif',
          }}
        >
          <Grid
            container
            spacing={{ xs: 1.5, sm: 2 }}
            alignItems="center"
          >
            {/* Filtro de prerregistro */}
            <Grid item xs={12} sm={5} md="auto">
              <Box
                sx={{
                  display: 'flex',
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: { xs: 0.75, sm: 1.5 },
                  width: '100%',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'Poppins, sans-serif',
                    fontWeight: 600,
                    color: '#020305',
                    whiteSpace: 'nowrap',
                    fontSize: '14px',
                  }}
                >
                  Prerregistro
                </Typography>

                <ToggleButtonGroup
                  size="small"
                  value={filtroPrerregistro}
                  exclusive
                  onChange={(_, val) => {
                    if (val) {
                      setFiltroPrerregistro(val);
                      setPage(1);
                    }
                  }}
                  sx={{
                    height: 36,
                    width: { xs: '100%', sm: 'auto' },
                    '& .MuiToggleButton-root': {
                      fontFamily: 'Poppins, sans-serif',
                      textTransform: 'none',
                      fontSize: { xs: '0.8rem', sm: '0.875rem' },
                      px: { xs: 1.5, sm: 2 },
                      flex: { xs: 1, sm: 'initial' },
                      whiteSpace: 'nowrap',
                      fontWeight: 500,
                      color: '#2C3E50',
                      '&.Mui-selected': {
                        backgroundColor: '#2266CC',
                        color: '#FFFFFF',
                        fontWeight: 600,
                        '&:hover': {
                          backgroundColor: '#1B52A4',
                        },
                      },
                    },
                  }}
                >
                  <ToggleButton value="Cualquiera">Cualquiera</ToggleButton>
                  <ToggleButton value="SI">Sí</ToggleButton>
                  <ToggleButton value="NO">No</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Grid>

            {/* Buscador */}
            <Grid item xs={12} sm md>
              <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Número documento / Nombre completo"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1);
                }}
                variant="outlined"
                sx={{
                  backgroundColor: '#FFF',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '4px',
                  },
                  '& .MuiInputBase-input': {
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: '14.5px',
                  },
                  '& .MuiInputBase-input::placeholder': {
                    fontFamily: 'Poppins, sans-serif',
                    opacity: 0.7,
                    fontSize: '14px',
                  },
                }}
              />
              <button
                type="button"
                onClick={limpiarFiltros}
                style={{
                  whiteSpace: 'nowrap',
                  padding: '0 16px',
                  border: '1px solid #2266CC',
                  borderRadius: '4px',
                  backgroundColor: '#FFFFFF',
                  color: '#2266CC',
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '14px',
                  cursor: 'pointer',
                }}
              >
                Limpiar filtros
              </button>
              </Box>
            </Grid>
          </Grid>
        </Box>

       {/* TABLA PRINCIPAL */}
        <div
          className={`${styleScss['table-limit']}`}
          style={{
            maxWidth: '100%',
            overflowWrap: 'break-word',
            overflowY: 'hidden',
          }}
        >
          <Stack padding={2}>
            <TableContainer
              component={Paper}
              variant='outlined'
              style={{
                overflowY: 'visible',
                overflowWrap: 'break-word',
                overflowX: 'auto',
              }}
            >
              <Table
                style={{
                  overflowY: 'visible',
                  minWidth: '700px',
                }}
              >
                <TableHead
                  className={`${styleScss['th-custom']}`}
                  sx={{
                    backgroundColor: '#2266CC',
                  }}
                >
                  <TableRow>
                    <TableCell
                      style={{
                        fontFamily: 'Poppins',
                        fontSize: '16.25px',
                        textAlign: 'center',
                        color: '#FFF',
                        fontWeight: 'bold',
                      }}
                    >
                      <h4>Tipo de documento</h4>
                    </TableCell>

                    <TableCell
                      style={{
                        fontFamily: 'Poppins',
                        fontSize: '16.25px',
                        textAlign: 'center',
                        color: '#FFF',
                        fontWeight: 'bold',
                      }}
                    >
                      <h4>Número de documento</h4>
                    </TableCell>

                    <TableCell
                      style={{
                        fontFamily: 'Poppins',
                        fontSize: '16.25px',
                        textAlign: 'center',
                        color: '#FFF',
                        fontWeight: 'bold',
                      }}
                    >
                      <h4>Nombre completo candidato</h4>
                    </TableCell>

                    <TableCell
                      style={{
                        fontFamily: 'Poppins',
                        fontSize: '16.25px',
                        textAlign: 'center',
                        color: '#FFF',
                        fontWeight: 'bold',
                      }}
                    >
                      <h4>Prerregistro</h4>
                    </TableCell>

                    <TableCell
                      style={{
                        fontFamily: 'Poppins',
                        fontSize: '16.25px',
                        textAlign: 'center',
                        color: '#FFF',
                        fontWeight: 'bold',
                      }}
                    >
                      <h4>Fecha de prerregistro</h4>
                    </TableCell>

                    <TableCell
                      style={{
                        fontFamily: 'Poppins',
                        fontSize: '16.25px',
                        textAlign: 'center',
                        color: '#FFF',
                        fontWeight: 'bold',
                      }}
                    >
                      <h4>Fecha asistencia</h4>
                    </TableCell>

                    <TableCell
                      style={{
                        fontFamily: 'Poppins',
                        fontSize: '16.25px',
                        textAlign: 'center',
                        color: '#FFF',
                        fontWeight: 'bold',
                      }}
                    >
                      <h4>Acciones</h4>
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {asistentes.length > 0 ? (
                    asistentes.map((row, index) => (
                      <TableRow
                        key={
                          row.id
                            ? `asistencia-${row.id}`
                            : `user-${row.user_id}-${index}`
                        }
                        hover
                      >
                        <TableCell
                          style={{
                            fontFamily: 'Poppins',
                            fontSize: '14.5px',
                            textAlign: 'center',
                          }}
                        >
                          {row.tipo_documento}
                        </TableCell>

                        <TableCell
                          style={{
                            fontFamily: 'Poppins',
                            fontSize: '14.5px',
                            textAlign: 'center',
                          }}
                        >
                          {row.numero_documento}
                        </TableCell>

                        <TableCell
                          style={{
                            fontFamily: 'Poppins',
                            fontSize: '14.5px',
                            textAlign: 'center',
                          }}
                        >
                          {row.nombre_completo}
                        </TableCell>

                        <TableCell
                          style={{
                            fontFamily: 'Poppins',
                            fontSize: '14.5px',
                            textAlign: 'center',
                          }}
                        >
                          {row.realizo_prerregistro === 'SI' ? 'Sí' : 'No'}
                        </TableCell>

                        <TableCell
                          style={{
                            fontFamily: 'Poppins',
                            fontSize: '14.5px',
                            textAlign: 'center',
                          }}
                        >
                          {formatFecha(row.fecha_prerregistro)}
                        </TableCell>

                        <TableCell
                          style={{
                            fontFamily: 'Poppins',
                            fontSize: '14.5px',
                            textAlign: 'center',
                          }}
                        >
                          {formatFecha(row.fecha_asistencia)}
                        </TableCell>

                        <TableCell
                          style={{
                            fontFamily: 'Poppins',
                            fontSize: '14.5px',
                            textAlign: 'center',
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={(e) => handleOpenMenu(e, row)}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        align="center"
                        sx={{
                          py: 3,
                          fontFamily: 'Poppins',
                          fontSize: '14.5px',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'Poppins',
                            fontSize: '14.5px',
                          }}
                        >
                          {loading
                            ? 'Cargando candidatos...'
                            : 'No se encontraron registros de asistencia.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </div>

        {/* MENÚ DESPLEGABLE DE ACCIONES */}
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
          <MenuItem
            onClick={() => selectedRow && handleConfirmarAsistencia(selectedRow)}
          >
            <CheckCircleOutlineIcon fontSize="small" sx={{ mr: 1, color: '#2266CC' }} />
            Confirmar asistencia
          </MenuItem>
        </Menu>

        {/* PAGINACIÓN */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, val) => setPage(val)}
              shape="rounded"
              size={isMobile ? 'small' : 'medium'}
            />
          </Box>
        )}

        {/* NOTIFICACIÓN TOAST (ESQUINA SUPERIOR DERECHA) */}
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          open={open}
          onClose={() => setOpen(false)}
        >
          <Alert
            onClose={() => setOpen(false)}
            variant="filled"
            severity={alert ? 'success' : 'error'}
            sx={{ whiteSpace: 'pre-line' }}
          >
            {alert ? <AlertTitle>Éxito</AlertTitle> : <AlertTitle>Error</AlertTitle>}
            {alertMsg}
          </Alert>
        </Snackbar>
      </div>
    </>
  );
};

export default RegistroAsistenciaEventoPage;